import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ImageLabelingContent } from "../content-types";
import { norm } from "../grading";
import { Explanation, Prompt } from "./shared";
import type { QuestionRendererProps } from "./types";

const CIRCLED = ["①", "②", "③", "④", "⑤", "⑥", "⑦", "⑧"];

function marker(n: number): string {
  return CIRCLED[n - 1] ?? `(${n})`;
}

/** image_labeling — "relabel the diagram". The image shows numbered markers
 *  (1..N) where part-labels were stripped; the learner assigns a label from the
 *  shared pool to each marker. Tap a marker to select it, then tap a pool label
 *  (labels are reusable). Per-marker grading. Mirrors iOS ImageLabelingView. */
export function ImageLabelingRenderer({
  content,
  answer,
  setAnswer,
  revealed,
}: QuestionRendererProps) {
  const { t } = useTranslation();
  const c = content as unknown as ImageLabelingContent;
  const markers = useMemo(
    () => [...(c.markers ?? [])].sort((a, b) => a.id - b.id),
    [c.markers],
  );
  const pool = c.label_pool ?? [];
  const [selected, setSelected] = useState<number | null>(markers[0]?.id ?? null);

  const picks = (answer as Record<string, string>) ?? {};
  const setPick = (id: number, label: string) => {
    setAnswer({ ...picks, [String(id)]: label });
  };

  const tapMarker = (id: number) => {
    if (revealed) return;
    if (picks[String(id)]) {
      const next = { ...picks };
      delete next[String(id)];
      setAnswer(next);
    }
    setSelected(id);
  };

  const tapLabel = (label: string) => {
    if (revealed) return;
    const target = selected ?? markers.find((m) => !picks[String(m.id)])?.id;
    if (target == null) return;
    setPick(target, label);
    const nextEmpty = markers.find((m) => m.id !== target && !picks[String(m.id)]);
    setSelected(nextEmpty?.id ?? null);
  };

  const markerCorrect = (id: number, correct: string): boolean =>
    norm(picks[String(id)] ?? "") === norm(correct);

  return (
    <div>
      <Prompt>{c.prompt || t("Match each numbered marker to the correct part.")}</Prompt>

      {c.image_url && (
        // eslint-disable-next-line jsx-a11y/img-redundant-alt
        <img
          src={c.image_url}
          alt={c.caption || "diagram to label"}
          className="mt-3 max-h-80 w-full rounded-xl object-contain"
        />
      )}
      {c.caption && <p className="mt-1.5 text-xs text-ink-faint">{c.caption}</p>}

      {/* One row per marker: number → assigned label (tap to select/clear). */}
      <div className="mt-4 space-y-2">
        {markers.map((m) => {
          const chosen = picks[String(m.id)] ?? "";
          const ok = revealed ? markerCorrect(m.id, m.correct) : null;
          return (
            <button
              key={m.id}
              type="button"
              disabled={revealed}
              onClick={() => tapMarker(m.id)}
              className={`flex w-full items-center gap-3 rounded-xl border px-3 py-2 text-left transition ${
                revealed
                  ? ok
                    ? "border-emerald-400 bg-emerald-50"
                    : "border-red-400 bg-red-50"
                  : selected === m.id
                    ? "border-brand-500 bg-brand-100 ring-1 ring-brand-300"
                    : "border-black/10 bg-white"
              }`}
            >
              <span className="text-lg font-semibold tabular-nums">{marker(m.id)}</span>
              <span className={chosen ? "font-medium" : "text-ink-faint"}>
                {chosen || t("Tap, then choose a label")}
              </span>
              {revealed && !ok && (
                <span className="ml-auto text-xs text-emerald-700">
                  {t("Answer:")} <span className="font-semibold">{m.correct}</span>
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Shared label pool. */}
      {!revealed && (
        <div className="mt-4">
          <p className="mb-2 text-xs font-medium text-ink-faint">
            {selected !== null
              ? t("Tap a label for the highlighted marker")
              : t("Tap a marker above, then a label")}
          </p>
          <div className="flex flex-wrap gap-2">
            {pool.map((label, k) => (
              <button
                key={k}
                type="button"
                onClick={() => tapLabel(label)}
                className="rounded-lg border border-brand-300/60 bg-white px-3.5 py-2 text-sm transition hover:bg-brand-50"
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      )}

      {revealed && <Explanation text={c.explanation} />}
    </div>
  );
}
